import type { EssentialMediaInfo } from '@module/types/play/goPlayTypes';
import convertFormData from '@module/utils/convertFormData';
import { fetchExtended } from '../../../utils/fetch/client-fetch';
import type { ApiBaseResponse } from '../../type';
import type {
  BjListParams,
  BjListResponse,
  FetchBjNoticeParams,
  FetchFanHideAddParams,
  FetchFanHideDeleteParams,
  FetchLiveBookmarkParams,
  FetchLiveBookmarkResponse,
  FetchLiveFanParams,
  FetchLiveFanResponse,
  FetchLiveGiftParams,
  FetchLiveGiftResponse,
  FetchLiveRoomRankParams,
  FetchLiveRoomRankResponse,
  FetchLiveUpcomingParams,
  FetchLiveUpcomingResponse,
  FetchLiveWatchParams,
  FetchLiveWatchResponse,
  FetchMemberInfoParams,
  FetchRtReportCategoryResponse,
  FetchWatchDeleteParams,
  LiveItems,
  LiveParams,
  LivePlayParams,
  LiveRankingParams,
  LiveRankingResponse,
  LiveUrl,
  LiveUrlParams,
  MemberInfoResponse,
  PlayInfo,
  PlayUserCount,
  PlayUserCountParams,
  PostLiveExcelSponParams,
  PostLiveExcelSponResponse,
  PostLiveRecommendParams,
  PostLiveRecommendResponse,
  PostLiveSponParams,
  PostLiveSponResponse,
  PostRtReportParams,
  PostRtReportResponse,
} from './type';

export async function fetchLive(fetchLiveParams: LiveParams) {
  const res = await fetchExtended(`/v1/live/index`, {
    method: 'POST',
    body: convertFormData(fetchLiveParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as LiveItems;
}

export async function fetchBjList(fetchLiveParams: BjListParams) {
  const res = await fetchExtended(`/v1/live/bj_list`, {
    method: 'POST',
    body: convertFormData(fetchLiveParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as BjListResponse;
}

export const fetchLiveRanking = async (liveRankingParams: LiveRankingParams) => {
  const res = await fetchExtended(`/v1/live/cache`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(liveRankingParams),
  });

  const result = await res.json();

  return result as LiveRankingResponse;
};

export async function fetchLivePlay(fetchLivePlayParams: LivePlayParams) {
  const res = await fetchExtended(`/v1/live/play`, {
    method: 'POST',
    body: convertFormData(fetchLivePlayParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as PlayInfo;
}

export const fetchPlayUserCount = async (params?: PlayUserCountParams) => {
  const res = await fetchExtended(`/v1/chat/channel_user_count?channel=${params?.channel}&token=${params?.token}`, {
    method: 'GET',
    credentials: 'omit',
  });

  const result = await res.json();

  return result as PlayUserCount;
};

export const fetchLiveUrl = async (liveUrlParams: LiveUrlParams) => {
  const res = await fetchExtended(`/v1/live/url`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(liveUrlParams),
  });

  const result = await res.json();

  return result as LiveUrl;
};

export const postLiveRecommend = async (postLiveRecommendParams: PostLiveRecommendParams) => {
  const res = await fetchExtended(`/v1/chat/recom`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(postLiveRecommendParams),
  });

  const result = await res.json();

  return result as PostLiveRecommendResponse;
};

export const fetchLiveRoomRank = async (fetchLiveRoomRankParams: FetchLiveRoomRankParams) => {
  const res = await fetchExtended(`/v1/live/room_rank`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(fetchLiveRoomRankParams),
  });

  const result = await res.json();

  return result as FetchLiveRoomRankResponse;
};

export const fetchMemberInfo = async (params?: FetchMemberInfoParams) => {
  const res = await fetchExtended(`/v1/member/info`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params && convertFormData(params),
  });

  const result = await res.json();

  return result as MemberInfoResponse;
};

export const postLiveSpon = async (postLiveSponParams: PostLiveSponParams, isASP: boolean = false) => {
  const res = await fetchExtended(`/v1/live/${isASP ? 'spon_asp' : 'spon'}`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(postLiveSponParams),
  });

  const result = await res.json();

  return result as PostLiveSponResponse;
};

export const postLiveExcelSpon = async (postLiveExcelSponParams: PostLiveExcelSponParams) => {
  const res = await fetchExtended(`/v1/live/excel_spon`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(postLiveExcelSponParams),
  });

  const result = await res.json();

  return result as PostLiveExcelSponResponse;
};

export async function fetchLiveFan(fetchLiveFanParams: FetchLiveFanParams) {
  const res = await fetchExtended(`/v1/live/fan`, {
    method: 'POST',
    body: convertFormData(fetchLiveFanParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as FetchLiveFanResponse;
}

export async function fetchLiveBookmark(fetchLiveBookmarkParams: FetchLiveBookmarkParams) {
  const res = await fetchExtended(`/v1/live/bookmark`, {
    method: 'POST',
    body: convertFormData(fetchLiveBookmarkParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as FetchLiveBookmarkResponse;
}

export async function fetchLiveGift(fetchLiveGiftParams: FetchLiveGiftParams) {
  const res = await fetchExtended(`/v1/live/gift`, {
    method: 'POST',
    body: convertFormData(fetchLiveGiftParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as FetchLiveGiftResponse;
}

export async function fetchLiveWatch(fetchLiveWatchParams: FetchLiveWatchParams) {
  const res = await fetchExtended(`/v1/live/watch`, {
    method: 'POST',
    body: convertFormData(fetchLiveWatchParams),
    credentials: 'include',
  });
  const result = await res.json();

  return result as FetchLiveWatchResponse;
}

export async function fetchLiveUpcoming(params: FetchLiveUpcomingParams) {
  const { bjUserIdx, limit } = params;
  const searchParams = new URLSearchParams();
  if (bjUserIdx) searchParams.append('bjUserIdx', String(bjUserIdx));
  if (limit) searchParams.append('limit', String(limit));
  const res = await fetchExtended(`/v1/live/upcoming?${searchParams.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });
  const result = (await res.json()) as FetchLiveUpcomingResponse;

  return result;
}

export const fetchFanHideAdd = async (params: FetchFanHideAddParams) => {
  const res = await fetchExtended(`/v1/pick/fan_hide_add`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(params),
  });

  const result = await res.json();

  return result as ApiBaseResponse;
};

export const fetchFanHideDelete = async (params: FetchFanHideDeleteParams) => {
  const res = await fetchExtended(`/v1/pick/fan_hide_delete`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(params),
  });

  const result = await res.json();

  return result as ApiBaseResponse;
};

export const fetchWatchDelete = async (params: FetchWatchDeleteParams) => {
  const res = await fetchExtended(`/v1/watch/delete`, {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(params),
  });

  const result = await res.json();

  return result as ApiBaseResponse;
};

export const fetchBjNotice = async (params?: FetchBjNoticeParams) => {
  const res = await fetchExtended(`/v1/manage/bj/notice?creatorIdx=${params?.creatorIdx}`, {
    method: 'GET',
    credentials: 'include',
  });

  const result = await res.json();
  return result as PlayUserCount;
};

// RT 대응 - 전환 후 삭제 예정
export async function fetchRtReportCategory() {
  const res = await fetchExtended('/v1/bbs/rt_report/category', {
    method: 'GET',
    credentials: 'include',
  });

  const result = await res.json();

  return result as FetchRtReportCategoryResponse;
}

export async function postRtReport(params: PostRtReportParams) {
  const res = await fetchExtended('/v1/bbs/rt_report', {
    method: 'POST',
    credentials: 'include',
    body: convertFormData(params),
  });

  const result = await res.json();

  return result as PostRtReportResponse;
}

export async function fetchRandomMediaInfo(signal?: AbortSignal): Promise<EssentialMediaInfo | null> {
  try {
    const res = await fetchExtended('/v1/live/getMediaRand', {
      method: 'GET',
      credentials: 'include',
      signal,
    });
    if (res.status !== 200) return null;

    const result = await res.json();
    const mediaUserId = String((result as any)?.mediaUserId ?? '');
    if (!mediaUserId) return null;

    const mediaInfo: EssentialMediaInfo = {
      userId: mediaUserId,
      userNick: '',
      isLive: true,
      isAdult: false,
      isPw: false,
      type: 'free',
      user: 0,
      userLimit: Infinity,
    };
    return mediaInfo;
  } catch (err) {
    console.error(err);
    // Silently ignore fetch/abort errors
    return null;
  }
}
